import { useState } from 'react';
import { Button, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { Replay, Send } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { emailApi, type EmailQueueRow } from '../../api/engine.api';
import { extractApiError } from '../../api/error';

export function EmailLogsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState<string | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['email-queue', page], queryFn: () => emailApi.listQueue({ page, limit: 25 }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['email-queue'] });

  const process = useMutation({ mutationFn: () => emailApi.process(), onSuccess: (r) => { setToast(`Processed ${r.processed} · sent ${r.sent} · failed ${r.failed}`); invalidate(); } });
  const retry = useMutation({ mutationFn: (id: number) => emailApi.retry(id), onSuccess: invalidate, onError: (e) => setToast(extractApiError(e)) });

  const columns: Column<EmailQueueRow>[] = [
    { key: 'to_address', label: 'To' },
    { key: 'subject', label: 'Subject' },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'attempts', label: 'Attempts', width: 100, render: (r) => `${r.attempts}/${r.max_attempts}` },
    { key: 'last_error', label: 'Last Error', render: (r) => r.last_error ?? '—' },
  ];

  return (
    <>
      <PageHeader title="Email Logs" subtitle="Queue-based email delivery with retry." action={<Button startIcon={<Send />} variant="contained" onClick={() => process.mutate()}>Process Queue</Button>} />
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={25} total={data?.meta?.total ?? 0} onPageChange={setPage} onLimitChange={() => undefined} rowKey={(r) => r.id}
        emptyTitle="No emails queued"
        actions={(r) => (r.status === 'failed' ? <Tooltip title="Retry"><IconButton size="small" onClick={() => retry.mutate(r.id)}><Replay fontSize="small" /></IconButton></Tooltip> : null)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
